import React, { useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { NavigateFunction } from "react-router-dom";
import { AxiosError } from "axios";
import { syncSite } from "../utils/syncSite";
import { useCurrentSiteSyncNeeded } from "../utils/syncStore";
import { createApi } from "../utils/createApi";
import { universalAPIErrorHandler } from "../utils/universalAPIErrorHandler";
import { shouldOfferRebootToApply } from "../utils/shouldOfferRebootToApply";
import { getNumbersAfterSites } from "../pages/EditCamera/EditCamera";
import { OrangeTextButton } from "./OrangeTextButton";

/**
 * Shared sync-from-an-edit-view flow, used by both the header Sync button
 * (EditViewSyncButton) and the unsynced-changes banner (SyncNeededBanner) so
 * they can't drift apart.
 *
 * Wraps the shared `syncSite` helper and the follow-up reboot decision: a
 * successful sync arms the one-shot `rebootSite` flag, and on an armed site we
 * offer to reboot now to apply — the same choice the About page gives.
 */

const readIntegrationTypeName = (): string | undefined => {
  try {
    return JSON.parse(localStorage.getItem("siteInfo") || "{}")
      .integration_type_name;
  } catch {
    return undefined;
  }
};

/**
 * Whether to offer the post-sync reboot (= arm), decided from FRESH site
 * state. The cached localStorage `siteInfo` was a snapshot from page load —
 * on the schedules edit view that snapshot predates the schedule save that
 * may have just disarmed the site, so it said `armed: true` for a site the
 * schedule had turned off, and the prompt offered an arm-outside-schedule
 * (SUP-311 / ENG-811). Refetch /about/ so both `armed` and `arm_intended`
 * are current; on fetch failure fall back to the cached snapshot's `armed`
 * only, mirroring the old behavior rather than silently dropping the offer.
 */
const shouldOfferReboot = async (siteId: string): Promise<boolean> => {
  const AxiosUI = createApi(``);
  try {
    const response = await AxiosUI.get(`customer/${siteId}/about/`);
    localStorage.setItem("siteInfo", JSON.stringify(response.data));
    return shouldOfferRebootToApply(response.data);
  } catch {
    try {
      const cached = JSON.parse(localStorage.getItem("siteInfo") || "{}");
      return shouldOfferRebootToApply(cached);
    } catch {
      return false;
    }
  }
};

const readHasFlexSchedule = (): boolean => {
  try {
    return !!JSON.parse(localStorage.getItem("siteInfo") || "{}")
      .has_flex_schedule;
  } catch {
    return false;
  }
};

export const useSyncSiteFlow = (navigate: NavigateFunction) => {
  const syncNeeded = useCurrentSiteSyncNeeded();
  const [syncing, setSyncing] = useState<boolean>(false);
  const [rebootPromptOpen, setRebootPromptOpen] = useState<boolean>(false);

  // Clips/autopatrol sites don't deploy connector settings, so syncing doesn't
  // apply — mirror the About-page SyncSiteButton guard.
  const integrationTypeName = readIntegrationTypeName();
  const syncApplies =
    integrationTypeName !== "clips" && integrationTypeName !== "autopatrol";

  const syncNow = async () => {
    if (syncing) return;
    setSyncing(true);
    // syncSite marks the store synced on success (or handleDeployNextFailure
    // re-marks it unsynced on failure) — consumers re-render from the store.
    await syncSite(getNumbersAfterSites(location.href), navigate);
    setSyncing(false);
    // On a successful sync (reboot flag armed) of an armed site that is meant
    // to be armed (schedule or manual arm),
    // offer to reboot now to apply — matching the About-page behavior so the
    // operator can finish the flow without leaving this page. Decided from a
    // fresh fetch, never the page-load snapshot (ENG-811).
    if (
      localStorage.getItem("rebootSite") === "true" &&
      (await shouldOfferReboot(getNumbersAfterSites(location.href)))
    ) {
      setRebootPromptOpen(true);
    }
  };

  const handleDeclineReboot = () => {
    setRebootPromptOpen(false);
    // The operator chose not to reboot now; clear the flag so the About page
    // doesn't re-prompt for the same synced change.
    localStorage.setItem("rebootSite", "false");
  };

  const handleReboot = async () => {
    setRebootPromptOpen(false);
    const siteId = getNumbersAfterSites(location.href);
    if (readHasFlexSchedule()) {
      // Flex-schedule sites can't be armed site-wide — the backend rejects
      // customer/{id}/action/arm for them without arming anything (ENG-389),
      // and the per-schedule picker lives on the About page. Keep the
      // one-shot rebootSite flag set and navigate there: the About page
      // consumes the flag and re-opens its RebootDialog, which routes flex
      // sites into the schedule picker.
      navigate(`/sites/aboutsite/${siteId}`);
      return;
    }
    localStorage.setItem("rebootSite", "false");
    const AxiosUI = createApi(``);
    // Rebooting a site is an arm (the connector restarts and re-reads its
    // settings from the cloud), but action/reboot/ records it as a
    // "Manual Reboot" in the action log instead of a "Manual Arm" the
    // operator never performed (ENG-1380 / SUP-433). No local state to
    // refresh — we're on an edit view, and the About page reloads fresh on
    // navigation.
    await AxiosUI.post(`customer/${siteId}/action/reboot/`).then(
      () => {},
      (reason: AxiosError) => {
        universalAPIErrorHandler(reason, navigate, handleReboot, () => {
          alert("Error rebooting site, please try again.");
        });
      }
    );
  };

  const rebootDialog = (
    <Dialog open={rebootPromptOpen} aria-labelledby="edit-view-reboot-title">
      <DialogTitle id="edit-view-reboot-title">Reboot Site?</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Your changes are synced to the cloud, but the camera connection is
          still running the previous settings. Would you like to reboot the site
          now to apply them? Otherwise the changes are applied the next time the
          site arms on its schedule.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <OrangeTextButton
          onClick={handleDeclineReboot}
          text="No"
          disabled={false}
        />
        <OrangeTextButton
          onClick={handleReboot}
          text="Reboot"
          disabled={false}
        />
      </DialogActions>
    </Dialog>
  );

  return { syncNeeded, syncing, syncApplies, syncNow, rebootDialog };
};
